import React, { useState, useEffect } from 'react';
import { X, Swords, Smartphone, Download, RefreshCw, AlertTriangle, Copy, Check } from 'lucide-react';
import { ParentalGate } from './Layouts';

interface AppLauncherModalProps {
  isOpen: boolean;
  onClose: () => void;
  payload: string;
  challengerName?: string;
  triggerOnlineAction?: (featureName: string, action: () => void) => void;
}

export const AppLauncherModal: React.FC<AppLauncherModalProps> = ({
  isOpen,
  onClose,
  payload,
  challengerName = 'A Friend',
  triggerOnlineAction
}) => {
  const [status, setStatus] = useState<'idle' | 'launching' | 'failed' | 'success'>('idle');
  const [copied, setCopied] = useState(false);
  const [gateAction, setGateAction] = useState<(() => void) | null>(null);

  const handleGateProtectedAction = (action: () => void) => {
    setGateAction(() => action);
  };

  const fullChallengeLink = `${window.location.origin}/?duel=${payload}`;
  const appUri = `web+bpq://?duel=${payload}`;
  const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.biblepuzzlequiz.twa';

  const triggerLaunch = () => {
    setStatus('launching');

    // Attempt to open the custom device protocol
    window.location.href = appUri;

    const start = Date.now();
    
    // Fallback timer: wait 2.5 seconds to see if focus was successfully lost (meaning app opened)
    const timer = setTimeout(() => {
      // If the browser remains actively in focus and visible, the custom protocol failed to launch
      if (document.hasFocus() && !document.hidden && (Date.now() - start < 3500)) {
        setStatus('failed');
      } else {
        setStatus('success');
      }
    }, 2500);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearTimeout(timer);
        setStatus('success');
      }
    };

    const handleBlur = () => {
      clearTimeout(timer);
      setStatus('success');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
    };
  };

  const fallbackCopyText = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      }
    } catch (err) {
      console.warn("Clipboard fallback failed", err);
    }
    document.body.removeChild(textArea);
  };

  const copyToClipboard = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(fullChallengeLink)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2500);
        })
        .catch(() => {
          fallbackCopyText(fullChallengeLink);
        });
    } else {
      fallbackCopyText(fullChallengeLink);
    }
  };

  // Reset state when modal is closed or opened with a new payload
  useEffect(() => {
    if (isOpen) {
      setStatus('idle');
      setCopied(false);
    }
  }, [isOpen, payload]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <ParentalGate 
        isOpen={!!gateAction} 
        onClose={() => setGateAction(null)} 
        onSuccess={() => {
          if (gateAction) gateAction();
          setGateAction(null);
        }}
        title="Adult Verification"
        message="To proceed to the Google Play Store, please have an adult/parent solve this equation:"
      />
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-blue-950/80 backdrop-blur-xl" 
        onClick={onClose}
      ></div>

      {/* Modal Card */}
      <div className="relative bg-white w-full max-w-md rounded-[2.5rem] p-8 md:p-10 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)] border border-blue-50/10 text-center animate-in zoom-in duration-300">
        
        {/* Close Button */}
        <button 
          onClick={onClose} 
          className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100/80 rounded-full transition cursor-pointer"
          aria-label="Close panel"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header Icon */}
        <div className="mx-auto mb-5 flex items-center justify-center w-16 h-16 rounded-3xl bg-blue-50 text-blue-600">
          <Swords className="w-8 h-8" />
        </div>

        {/* Title */}
        <h2 className="font-cinzel font-bold text-xl text-gray-900 mb-2 leading-snug">
          Faith Duel Invitation
        </h2>
        <p className="text-[10px] text-blue-600 font-bold uppercase tracking-widest mb-6">
          from <span className="text-gray-900 font-black">{challengerName}</span>
        </p>

        {/* Main interactive state layouts */}
        {status === 'idle' && (
          <div className="space-y-6">
            <div className="bg-gray-50/80 rounded-[2.5rem] p-6 border border-gray-100/50">
              <p className="text-sm font-medium text-gray-700 leading-relaxed">
                Choose how you'd like to accept this puzzle challenge on your device:
              </p>
            </div>

            <div className="flex flex-col gap-3">
              {/* Option 1: Open Link (Launches App) */}
              <button 
                onClick={triggerLaunch}
                className="w-full bg-blue-700 hover:bg-blue-800 text-white py-4.5 px-6 rounded-2xl font-bold shadow-lg shadow-blue-500/10 transition active:scale-95 flex items-center justify-center gap-2 cursor-pointer text-sm"
              >
                <Smartphone className="w-4 h-4" />
                Open Link (Installed App)
              </button>

              {/* Option 2: Copy Link (For pasting inside app) */}
              <button 
                onClick={copyToClipboard}
                className="w-full bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700 py-4 px-6 rounded-2xl font-bold transition active:scale-95 flex items-center justify-center gap-2 cursor-pointer text-sm"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-600 animate-pulse" /> : <Copy className="w-4 h-4 text-gray-500" />}
                {copied ? 'Link Copied Successfully!' : 'Copy Link (Paste Manually)'}
              </button>
            </div>

            {copied && (
              <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider animate-in fade-in duration-300">
                ✨ Paste this URL directly in-app under Settings → Faith Duel Accept!
              </p>
            )}
          </div>
        )}

        {status === 'launching' && (
          <div className="space-y-6">
            <div className="bg-gray-50/80 rounded-[2.5rem] p-8 border border-gray-100 flex flex-col items-center justify-center min-h-[160px]">
              <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mb-4" />
              <p className="text-sm font-bold text-gray-800">Searching for downloaded app...</p>
              <p className="text-[11px] text-gray-500 leading-normal max-w-[280px] text-center mt-2 font-medium">
                We are searching for <strong>Bible Puzzle Quiz</strong> client on this device to load the challenge directly.
              </p>
            </div>
            
            <button 
              onClick={() => setStatus('idle')}
              className="text-xs text-gray-500 font-bold hover:underline py-2"
            >
              Cancel & Go Back
            </button>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-6">
            <div className="bg-emerald-50/50 rounded-[2.5rem] p-6 border border-emerald-100 flex flex-col items-center justify-center min-h-[160px]">
              <div className="bg-emerald-100 p-2.5 rounded-2xl text-emerald-600 mb-3">
                <Check className="w-6 h-6 animate-pulse" />
              </div>
              <p className="text-sm font-bold text-emerald-950">App Search Complete</p>
              <p className="text-xs text-emerald-800 leading-relaxed max-w-[300px] mt-2">
                Launch request triggered on your device device! If the app didn't boot, click below to try again or play here.
              </p>
            </div>

            <div className="flex flex-col gap-2.5">
              <button 
                onClick={triggerLaunch}
                className="w-full bg-blue-50 hover:bg-blue-100/80 border border-blue-200 text-blue-700 py-4 px-6 rounded-2xl font-bold transition active:scale-95 text-xs cursor-pointer"
              >
                Try Opening App Again
              </button>
            </div>
          </div>
        )}

        {status === 'failed' && (
          <div className="space-y-6 animate-in slide-in-from-top-4 duration-300">
            <div className="bg-rose-50/50 rounded-[2.5rem] p-6 border border-rose-100/50 flex flex-col items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-rose-500 mb-3" />
              <p className="text-sm font-bold text-rose-950">App Not Detected</p>
              <p className="text-xs text-gray-500 leading-relaxed max-w-[320px] mt-2 font-medium">
                We searched for <strong>Bible Puzzle Quiz</strong> on your device but could not find it. It might not be installed or direct launching was restricted.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <button 
                onClick={() => {
                  const playStoreAction = () => handleGateProtectedAction(() => {
                    window.open(playStoreUrl, '_blank');
                  });
                  if (triggerOnlineAction) {
                    triggerOnlineAction('Play Store Download', playStoreAction);
                  } else {
                    playStoreAction();
                  }
                }}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-4 px-6 rounded-2xl font-bold shadow-lg shadow-blue-500/15 transition active:scale-95 flex items-center justify-center gap-2 cursor-pointer text-sm font-sans"
              >
                <Download className="w-5 h-5" />
                Get on Google Play Store
              </button>

              <button 
                onClick={copyToClipboard}
                className="w-full bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700 py-3 px-6 rounded-2xl font-bold transition active:scale-95 flex items-center justify-center gap-2 cursor-pointer text-xs"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-gray-400" />}
                {copied ? 'Link Copied!' : 'Copy Link for Manual Paste'}
              </button>
            </div>

            <p className="text-[10px] text-gray-400 font-medium leading-relaxed max-w-[300px] mx-auto">
              💡 After downloading, click the shared link again to open directly, or paste the copied code manually inside the app settings.
            </p>
          </div>
        )}

        {/* Version branding footer */}
        <div className="mt-8 pt-6 border-t border-gray-50 flex flex-col items-center justify-center">
          <p className="text-[9px] text-gray-400 font-mono tracking-wider">
            BIBLE PUZZLE QUIZ v(24)24
          </p>
        </div>

      </div>
    </div>
  );
};
